import { useEffect, useRef, useState } from "react";
import { Avatar, Checkbox, Field } from "../design";
import { GuarantorStatusBadge } from "../TenantDetails/GuarantorCardParts";
import "./GuarantorSelect.css";

/**
 * Multi-select field for the tenant's guarantors. The list stays open while
 * picking and closes on an outside click or Escape.
 * guarantors: [{ id, name, email, statusData }] (statusData from
 *   getGuarantorStatus).
 * value: the selected ids. onChange(ids).
 */
const GuarantorSelect = ({
  id,
  label,
  hint,
  placeholder,
  guarantors,
  value,
  onChange,
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointer = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handlePointer);
    return () => document.removeEventListener("mousedown", handlePointer);
  }, [open]);

  const toggle = (guarantorId) => {
    onChange(
      value.includes(guarantorId)
        ? value.filter((item) => item !== guarantorId)
        : [...value, guarantorId]
    );
  };

  const selectedNames = guarantors
    .filter((guarantor) => value.includes(guarantor.id))
    .map((guarantor) => guarantor.name)
    .join(", ");

  return (
    <Field
      htmlFor={id}
      label={label}
      hint={hint}
      className={`guarantorSelect ${className}`.trim()}
    >
      <div
        ref={rootRef}
        className="guarantorSelect__wrap"
        onKeyDown={(event) => {
          if (event.key === "Escape" && open) {
            // keep the popup around the field open
            event.stopPropagation();
            setOpen(false);
          }
        }}
      >
        <button
          id={id}
          type="button"
          className={`ds-control ds-control--md ds-select guarantorSelect__trigger${
            open ? " is-open" : ""
          }${selectedNames ? "" : " is-placeholder"}`}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
        >
          <span className="ds-control__input guarantorSelect__value">
            {selectedNames || placeholder}
          </span>
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            className="ds-select__chevron guarantorSelect__chevron"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        {open && (
          <div
            className="guarantorSelect__panel"
            role="listbox"
            aria-multiselectable="true"
          >
            {guarantors.map((guarantor) => {
              const selected = value.includes(guarantor.id);
              return (
                <div
                  key={guarantor.id}
                  role="option"
                  aria-selected={selected}
                  className={`guarantorSelect__option${
                    selected ? " is-selected" : ""
                  }`}
                  onClick={() => toggle(guarantor.id)}
                >
                  <Checkbox
                    checked={selected}
                    onChange={() => toggle(guarantor.id)}
                    ariaLabel={guarantor.name}
                  />
                  <Avatar name={guarantor.name} size="sm" />
                  <span className="guarantorSelect__text">
                    <span
                      className="guarantorSelect__name"
                      title={guarantor.name}
                    >
                      {guarantor.name}
                    </span>
                    <span
                      className="guarantorSelect__email"
                      title={guarantor.email}
                    >
                      {guarantor.email}
                    </span>
                  </span>
                  <GuarantorStatusBadge statusData={guarantor.statusData} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Field>
  );
};

export default GuarantorSelect;
